import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input, Spin, Typography, Tag, Empty } from 'antd';
import { SearchOutlined, FileTextOutlined, CalendarOutlined } from '@ant-design/icons';
import { searchAPI } from '../services/api';

interface SearchResult {
  schedules: Array<{
    id: number;
    test_number: string;
    title: string;
    status: string;
    company_name: string;
    score: number;
    highlight: Record<string, string>;
  }>;
  protocols: Array<{
    id: number;
    protocol_code: string;
    title: string;
    test_crop: string;
    score: number;
    highlight: Record<string, string>;
  }>;
}

interface Props {
  onNavigate: (path: string, label: string) => void;
}

const GlobalSearch: React.FC<Props> = ({ onNavigate }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(null);

  const doSearch = useCallback(async (q: string) => {
    if (!q.trim()) {
      setResults(null);
      return;
    }
    setLoading(true);
    try {
      const res = await searchAPI.global(q.trim());
      setResults(res.data);
      setShowDropdown(true);
    } catch {
      setResults(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setQuery(v);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => doSearch(v), 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Ctrl+K 단축키
  useEffect(() => {
    const handleGlobalKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleGlobalKey);
    return () => document.removeEventListener('keydown', handleGlobalKey);
  }, []);

  const renderHighlight = (text: string, highlight?: string) => {
    if (highlight) {
      return <span dangerouslySetInnerHTML={{ __html: highlight }} />;
    }
    return <>{text}</>;
  };

  const totalCount = (results?.schedules?.length || 0) + (results?.protocols?.length || 0);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <Input
        ref={inputRef as any}
        prefix={<SearchOutlined style={{ color: '#4f46e5', fontSize: 15 }} />}
        placeholder="통합 검색 — 동의어/오타보정 지원 (OpenSearch)"
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => results && setShowDropdown(true)}
        allowClear
        style={{
          width: 380, borderRadius: 10,
          background: '#eef2ff', border: '1.5px solid #c7d2fe',
          fontSize: 13,
        }}
        suffix={loading ? <Spin size="small" /> : <span style={{ fontSize: 10, color: '#818cf8', fontWeight: 600 }}>Ctrl+K</span>}
      />

      {showDropdown && query.trim() && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          marginTop: 4, background: '#fff', borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb',
          maxHeight: 420, overflowY: 'auto', zIndex: 1000,
        }}>
          {totalCount === 0 && !loading && (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <Empty description={`'${query}' 검색 결과 없음`} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          )}

          {results?.schedules && results.schedules.length > 0 && (
            <div>
              <div style={{ padding: '10px 16px 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
                <CalendarOutlined style={{ color: '#4f46e5', fontSize: 12 }} />
                <Typography.Text style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' as const }}>
                  시험일정 ({results.schedules.length})
                </Typography.Text>
              </div>
              {results.schedules.map((s) => (
                <div
                  key={`s-${s.id}`}
                  onClick={() => { onNavigate('/schedules', '시험일정총괄표'); setShowDropdown(false); setQuery(''); }}
                  style={{
                    padding: '8px 16px', cursor: 'pointer',
                    borderBottom: '1px solid #f8fafc',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Tag color={s.status === '진행중' ? 'blue' : s.status === '완료' ? 'green' : 'default'} style={{ fontSize: 11, margin: 0 }}>
                      {s.status}
                    </Tag>
                    <Typography.Text style={{ fontSize: 13, fontWeight: 600 }}>
                      {renderHighlight(s.test_number, s.highlight?.test_number)}
                    </Typography.Text>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {renderHighlight(s.title, s.highlight?.title)}
                    {s.company_name && <span style={{ marginLeft: 8, color: '#94a3b8' }}>{s.company_name}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {results?.protocols && results.protocols.length > 0 && (
            <div>
              <div style={{ padding: '10px 16px 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
                <FileTextOutlined style={{ color: '#7c3aed', fontSize: 12 }} />
                <Typography.Text style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase' as const }}>
                  시험계획서 ({results.protocols.length})
                </Typography.Text>
              </div>
              {results.protocols.map((p) => (
                <div
                  key={`p-${p.id}`}
                  onClick={() => { onNavigate('/protocols', '시험계획서 목록'); setShowDropdown(false); setQuery(''); }}
                  style={{
                    padding: '8px 16px', cursor: 'pointer',
                    borderBottom: '1px solid #f8fafc',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Typography.Text style={{ fontSize: 13, fontWeight: 600 }}>
                      {renderHighlight(p.protocol_code, p.highlight?.protocol_code)}
                    </Typography.Text>
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {renderHighlight(p.title, p.highlight?.title)}
                    {p.test_crop && <Tag style={{ fontSize: 10, marginLeft: 8 }}>{p.test_crop}</Tag>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalCount > 0 && (
            <div style={{ padding: '8px 16px', borderTop: '1px solid #f1f5f9', textAlign: 'center' }}>
              <Typography.Text style={{ fontSize: 11, color: '#94a3b8' }}>
                OpenSearch (nori + 동의어 + 오타보정)
              </Typography.Text>
            </div>
          )}
        </div>
      )}

      <style>{`
        .global-search-result mark {
          background: #fef08a;
          padding: 0 2px;
          border-radius: 2px;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};

export default GlobalSearch;
